import { fieldTypes } from "@ucl/library";
import {
  BoltApiClient,
  IBoltTask,
  IBoltTaskTemplate,
  INewBoltTask,
  INewBoltTaskRule,
  TaskExecutionTypeFieldSchema,
} from "@bolt/core";

export class BoltTaskApiClient {
  constructor(private readonly boltApiClient: BoltApiClient) {}

  async createTask(task: INewBoltTask) {
    return this.boltApiClient.post<INewBoltTask>("task/tasks", task);
  }

  async getTasks(parentEntityType: string, parentEntityId: string, onlyIncludeOpenTasks: boolean = false, entityType?: string, entityId?: string) {
    return this.boltApiClient.get<IBoltTask[]>(`task/tasks/${parentEntityType}/${parentEntityId}`, {
      params: {
        onlyIncludeOpenTasks,
        entityType,
        entityId,
      },
    });
  }

  async completeTask(
    taskId: string,
    updatedTaskExecutionTypeFields: TaskExecutionTypeFieldSchema
  ) {
    return this.boltApiClient.put(`task/tasks/${taskId}/complete`, {
      taskExecutionTypeFields: updatedTaskExecutionTypeFields,
    });
  }

  async withdrawTask(taskId: string, reason: string) {
    return this.boltApiClient.put(`task/tasks/${taskId}/status/withdraw`, {
      withdrawReason: reason,
    });
  }

  async reinstateTask(taskId: string) {
    return this.boltApiClient.put(`task/tasks/${taskId}/status/reinstate`);
  }

  async updateTask(taskId: string, value: IBoltTask) {
    return this.boltApiClient.put(`task/tasks/${taskId}`, value);
  }

  async withdrawMultiStepItemEndWorkFlow(itemId: string, batchId: string) {
    return this.boltApiClient.put(
      `task/${itemId}/status/withdraw/multistep/${batchId}/end`
    );
  }

  async withdrawMultiStepItemAndSkipToNextStepInWorkFlow(
    itemId: string,
    batchId: string
  ) {
    return this.boltApiClient.put(
      `task/${itemId}/status/withdraw/multistep/${batchId}/next`
    );
  }

  async reassignTask(taskId: string, assigneeId: string) {
    return this.boltApiClient.put(`task/tasks/${taskId}/assignee`, {
      assigneeId,
    });
  }

  // Task Templates
  async getTaskTypeById(id: string): Promise<IBoltTaskTemplate> {
    return this.boltApiClient.get<IBoltTaskTemplate>(
      `/task/task-templates/${id}`
    );
  }
  async getActionTypes(): Promise<fieldTypes.Option<string>[]> {
    return this.boltApiClient.get<fieldTypes.Option<string>[]>(
      "/task/task-templates/task-execution-types"
    );
  }

  async getTaskTypesByBatchId(batchId: string): Promise<IBoltTaskTemplate[]> {
    return this.boltApiClient.get<IBoltTaskTemplate[]>(
      `task/task-templates/batch/${batchId}`
    );
  }

  async createSingleStepTaskType(
    taskType: IBoltTaskTemplate
  ): Promise<IBoltTaskTemplate> {
    return this.boltApiClient.post<IBoltTaskTemplate>(
      "task/task-templates/single-step",
      taskType
    );
  }

  async updateSingleStepTaskType(
    taskType: IBoltTaskTemplate
  ): Promise<IBoltTaskTemplate> {
    return this.boltApiClient.put<IBoltTaskTemplate>(
      `task/task-templates/single-step/${taskType.id}`,
      taskType
    );
  }

  async createMultiStepTaskTypes(
    taskTypes: IBoltTaskTemplate[]
  ): Promise<IBoltTaskTemplate[]> {
    return this.boltApiClient.post<IBoltTaskTemplate[]>(
      "task/task-templates/multi-step",
      taskTypes
    );
  }

  async updateMultiStepTaskTypes(
    taskTypes: IBoltTaskTemplate[]
  ): Promise<IBoltTaskTemplate[]> {
    return this.boltApiClient.put<IBoltTaskTemplate[]>(
      "task/task-templates/multi-step",
      taskTypes
    );
  }

  // Task Rules

  async getTaskRuleById(taskRuleId: string) {
    return this.boltApiClient.get<INewBoltTaskRule>(
      `task/task-rules/${taskRuleId}`
    );
  }

  async createTaskRule(taskRule: INewBoltTaskRule) {
    return this.boltApiClient.post<INewBoltTaskRule>(
      "task/task-rules",
      taskRule
    );
  }

  async updateTaskRule(taskRuleId: string, taskRule: INewBoltTaskRule) {
    return this.boltApiClient.put<INewBoltTaskRule>(
      `task/task-rules/${taskRuleId}`,
      taskRule
    );
  }
}
export const createBoltTaskApiClient = (boltApiClient: BoltApiClient) => {
  return new BoltTaskApiClient(boltApiClient);
};
